<template>
  <div class="h-full flex flex-col overflow-hidden bg-white">
    <div v-if="loading" class="flex-1 flex flex-col">
      <div class="px-4 py-3 border-b border-gray-100">
        <div class="flex gap-3">
          <div v-for="n in 2" :key="'sk-score-' + n" class="flex-1 rounded-lg p-3 bg-gray-50">
            <div class="h-2.5 w-16 bg-gray-200 rounded animate-pulse mb-2"></div>
            <div class="h-7 w-12 bg-gray-200 rounded animate-pulse mb-1"></div>
            <div class="h-2.5 w-20 bg-gray-100 rounded animate-pulse"></div>
          </div>
        </div>
      </div>
      <div class="px-4 py-3 border-b border-gray-100">
        <div class="h-2.5 w-24 bg-gray-200 rounded animate-pulse mb-2"></div>
        <div class="space-y-1.5">
          <div class="h-3 bg-gray-100 rounded animate-pulse w-full"></div>
          <div class="h-3 bg-gray-100 rounded animate-pulse w-4/5"></div>
        </div>
      </div>
      <div class="px-4 py-3">
        <div class="h-2.5 w-20 bg-gray-200 rounded animate-pulse mb-2"></div>
        <div class="flex flex-wrap gap-1.5">
          <div v-for="n in 4" :key="'sk-rf-' + n" class="h-6 rounded-full bg-gray-100 animate-pulse" :style="{ width: [80, 100, 70, 90][n - 1] + 'px' }"></div>
        </div>
      </div>
    </div>

    <div v-else-if="!prediction" class="flex-1 flex items-center justify-center px-6">
      <div class="text-center">
        <FontAwesomeIcon :icon="['fas', 'chart-line']" class="text-2xl text-gray-300 mb-2" />
        <p class="text-xs text-gray-400">No readmission risk data available for this patient</p>
      </div>
    </div>

    <template v-else>
      <!-- Scores comparison -->
      <div class="px-4 py-3 border-b border-gray-100">
        <div class="flex gap-3">
          <!-- AI Risk Score -->
          <div class="flex-1 rounded-lg p-3" :class="riskBgLight(prediction.READMIT_RISK)">
            <div class="text-[10px] uppercase tracking-wider text-gray-500 font-medium">AI Risk Score</div>
            <div class="text-2xl font-bold mt-0.5" :class="riskText(prediction.READMIT_RISK)">
              {{ (prediction.READMIT_RISK * 100).toFixed(0) }}%
            </div>
            <div class="text-[10px] mt-0.5" :class="riskText(prediction.READMIT_RISK)">
              {{ prediction.CONFIDENCE }} confidence
            </div>
          </div>
          <!-- LACE Score -->
          <div class="flex-1 rounded-lg p-3 bg-gray-50">
            <div class="text-[10px] uppercase tracking-wider text-gray-500 font-medium">LACE Score</div>
            <template v-if="prediction.LACE_SCORE != null">
              <div class="text-2xl font-bold mt-0.5 text-gray-700">
                {{ prediction.LACE_SCORE }}<span class="text-sm font-normal text-gray-400"> / 16</span>
              </div>
              <div class="text-[10px] text-gray-400 mt-0.5">
                {{ prediction.LACE_SCORE <= 8 ? 'Low risk' : prediction.LACE_SCORE <= 12 ? 'Moderate risk' : 'High risk' }}
              </div>
            </template>
            <template v-else>
              <div class="text-2xl font-bold mt-0.5 text-gray-300">—</div>
              <div class="text-[10px] text-gray-400 mt-0.5">Not scored</div>
            </template>
          </div>
          <!-- Outcome (if known) -->
          <div v-if="prediction.READMITTED != null" class="flex-1 rounded-lg p-3" :class="prediction.READMITTED ? 'bg-red-50' : 'bg-green-50'">
            <div class="text-[10px] uppercase tracking-wider text-gray-500 font-medium">Outcome</div>
            <div class="text-2xl font-bold mt-0.5" :class="prediction.READMITTED ? 'text-red-700' : 'text-green-700'">
              {{ prediction.READMITTED ? '✗' : '✓' }}
            </div>
            <div class="text-[10px] mt-0.5" :class="prediction.READMITTED ? 'text-red-500' : 'text-green-600'">
              {{ prediction.READMITTED ? 'Readmitted' : 'Not readmitted' }}
            </div>
          </div>
        </div>
      </div>

      <!-- Model Reasoning -->
      <div class="px-4 py-3 border-b border-gray-100">
        <div class="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">Model Reasoning</div>
        <p class="text-xs text-gray-700 leading-relaxed">{{ prediction.REASONING }}</p>
      </div>

      <!-- Risk Factors -->
      <div class="px-4 py-3 border-b border-gray-100">
        <div class="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">Risk Factors</div>
        <div class="flex flex-wrap gap-1.5">
          <span
            v-for="(rf, i) in riskFactors"
            :key="i"
            class="px-2.5 py-1 bg-amber-50 text-amber-800 text-xs rounded-full border border-amber-200"
          >{{ rf }}</span>
        </div>
      </div>

      <!-- What LACE Misses -->
      <div v-if="prediction.WHAT_LACE_MISSES" class="px-4 py-3">
        <div class="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">What LACE Misses</div>
        <p class="text-xs text-gray-500 leading-relaxed">{{ prediction.WHAT_LACE_MISSES }}</p>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'AIRiskDetail',
  components: { FontAwesomeIcon },

  props: {
    patientIdentifier: { type: String, default: null }
  },

  data() {
    return {
      loading: false,
      prediction: null
    };
  },

  computed: {
    riskFactors() {
      if (!this.prediction?.RISK_FACTORS) return [];
      const rf = this.prediction.RISK_FACTORS;
      if (Array.isArray(rf)) return rf;
      try { return JSON.parse(rf); } catch { return []; }
    }
  },

  watch: {
    patientIdentifier: {
      immediate: true,
      handler: 'fetchRisk'
    }
  },

  methods: {
    async fetchRisk() {
      if (!this.patientIdentifier) {
        this.prediction = null;
        return;
      }

      this.loading = true;
      try {
        const { data } = await axios.get('/api/care-management/readmission-risk/', {
          params: { mbi: this.patientIdentifier }
        });
        const match = (data.predictions || []).find(p => p.MBI === this.patientIdentifier);
        this.prediction = match || null;
      } catch {
        this.prediction = null;
      } finally {
        this.loading = false;
      }
    },

    riskBgLight(score) {
      if (score >= 0.7) return 'bg-red-50';
      if (score >= 0.4) return 'bg-amber-50';
      return 'bg-green-50';
    },

    riskText(score) {
      if (score >= 0.7) return 'text-red-700';
      if (score >= 0.4) return 'text-amber-700';
      return 'text-green-700';
    }
  }
};
</script>
